import { z } from 'zod'
import { createdAtSortQueryShape, creatorListQueryShape } from '../list-query/contract.ts'
import { accountDisplaySchema } from '../profile/contract.ts'
import { roomBatchCreateSchema } from '../room/contract.ts'

export const operationModeSchema = z.enum(['agent', 'selfOperated'])

export const siteListSchema = z.object({
  current: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
  id: z.number().int().positive().optional(),
  keyword: z.string().trim().max(255).optional(),
  organizationId: z.number().int().positive().optional(),
  operationMode: operationModeSchema.optional(),
  status: z.enum(['enabled', 'disabled']).optional(),
  ...creatorListQueryShape,
  ...createdAtSortQueryShape
})

const siteBaseSchema = z.object({
  name: z.string().trim().min(1, '场地名称不能为空').max(255),
  organizationId: z.number().int().positive().optional().nullable(),
  operationMode: operationModeSchema,
  provinceCode: z
    .string()
    .trim()
    .regex(/^\d{6}$/, '省级行政区编码格式不正确'),
  provinceName: z.string().trim().min(1).max(50),
  cityCode: z
    .string()
    .trim()
    .regex(/^\d{6}$/, '城市行政区编码格式不正确'),
  cityName: z.string().trim().min(1).max(50),
  districtCode: z
    .string()
    .trim()
    .regex(/^\d{6}$/, '区县行政区编码格式不正确')
    .optional()
    .nullable(),
  districtName: z.string().trim().max(50).optional().nullable(),
  detailAddress: z.string().trim().min(1, '详细地址不能为空').max(500),
  note: z.string().trim().max(5000).optional().nullable()
})

export const siteUpsertSchema = siteBaseSchema
  .refine((data) => Boolean(data.districtCode) === Boolean(data.districtName), {
    message: '区县编码和名称必须同时填写',
    path: ['districtCode']
  })
  .superRefine((data, context) => {
    if (data.operationMode === 'selfOperated' && !data.organizationId) {
      context.addIssue({
        code: 'custom',
        message: '自营场地必须选择归属主体',
        path: ['organizationId']
      })
    }
    if (data.operationMode === 'agent' && data.organizationId) {
      context.addIssue({
        code: 'custom',
        message: '代理场地不能绑定经营主体',
        path: ['organizationId']
      })
    }
  })
  .superRefine((data, context) => {
    if (data.cityCode.slice(0, 2) !== data.provinceCode.slice(0, 2)) {
      context.addIssue({
        code: 'custom',
        message: '城市与省份行政区编码不匹配',
        path: ['cityCode']
      })
    }
    const districtBelongsToCity =
      data.cityCode === data.provinceCode
        ? data.districtCode?.slice(0, 2) === data.cityCode.slice(0, 2)
        : data.districtCode?.slice(0, 4) === data.cityCode.slice(0, 4)
    if (data.districtCode && !districtBelongsToCity) {
      context.addIssue({
        code: 'custom',
        message: '区县与城市行政区编码不匹配',
        path: ['districtCode']
      })
    }
  })

export const siteUpsertRequestSchema = z.object({
  id: z.number().int().positive().optional(),
  data: siteUpsertSchema
})

export const siteSetDisabledSchema = z.object({
  id: z.number().int().positive(),
  isDisabled: z.boolean()
})
export const siteBatchSetDisabledSchema = z.object({
  ids: z.array(z.number().int().positive()).min(1, '至少选择一个场地').max(100, '单次最多处理 100 个场地'),
  isDisabled: z.boolean()
})

export const siteSchema = siteBaseSchema.extend({
  id: z.number().int().positive(),
  organizationId: z.number().int().positive().nullable(),
  districtCode: z.string().nullable(),
  districtName: z.string().nullable(),
  note: z.string().nullable(),
  isDisable: z.boolean(),
  organization: z
    .object({
      id: z.number().int().positive(),
      name: z.string(),
      isDisable: z.boolean()
    })
    .nullable(),
  creator: accountDisplaySchema,
  createdAt: z.date(),
  updatedAt: z.date()
})

export const siteListOutputSchema = z.object({
  data: z.array(
    siteSchema.extend({ roomCount: z.number().int().nonnegative(), availableRoomCount: z.number().int().nonnegative() })
  ),
  total: z.number().int().nonnegative(),
  current: z.number().int().positive(),
  pageSize: z.number().int().positive()
})
export const siteOptionsInputSchema = z.object({
  keyword: z.string().trim().max(255).optional(),
  organizationId: z.number().int().positive().optional(),
  operationMode: operationModeSchema.optional(),
  status: z.enum(['enabled', 'disabled']).default('enabled')
})
export const siteOptionSchema = z.object({
  label: z.string(),
  value: z.number().int().positive(),
  organization: z.object({ id: z.number().int().positive(), name: z.string(), isDisable: z.boolean() }).nullable()
})
export const siteBatchOperationResultSchema = z.object({ count: z.number().int().nonnegative() })

export type SiteListInput = z.infer<typeof siteListSchema>
export type SiteOptionsInput = z.infer<typeof siteOptionsInputSchema>
export type SiteUpsertInput = z.infer<typeof siteUpsertSchema>
export type SiteSetDisabledInput = z.infer<typeof siteSetDisabledSchema>
export type SiteBatchSetDisabledInput = z.infer<typeof siteBatchSetDisabledSchema>

export const siteBatchDeleteSchema = z.object({
  ids: z.array(z.number().int().positive()).min(1, '至少选择一个场地').max(100, '单次最多删除 100 个场地')
})

export const siteBatchSetOwnershipSchema = z.discriminatedUnion('operationMode', [
  z.object({
    ids: siteBatchSetDisabledSchema.shape.ids,
    operationMode: z.literal('selfOperated'),
    organizationId: z.number().int().positive('请选择归属主体')
  }),
  z.object({
    ids: siteBatchSetDisabledSchema.shape.ids,
    operationMode: z.literal('agent'),
    organizationId: z.null().optional()
  })
])
export type SiteBatchSetOwnershipInput = z.infer<typeof siteBatchSetOwnershipSchema>

// 名称由服务端派生，避免客户端名称与地址不一致。
export const siteQuickCreateSchema = z.object({
  site: siteBaseSchema
    .omit({ name: true })
    .transform((data) => ({
      ...data,
      name: data.detailAddress
    }))
    .pipe(siteUpsertSchema),
  rooms: roomBatchCreateSchema.omit({ siteId: true, status: true, note: true })
})
export type SiteQuickCreateInput = z.input<typeof siteQuickCreateSchema>
export const siteQuickCreateOutputSchema = z.object({ site: siteSchema, created: z.number().int().positive() })
